/**
 * EmptyState — empty / zero-state placeholder.
 *
 * Design system mapping:
 *   • Centered column layout, py-12 px-6
 *   • Icon: size-12 text-webex-gray-400 dark: webex-gray-500
 *   • Title: text-base font-medium text-webex-gray-1000 dark: white (Webex H2: 16/500)
 *   • Description: text-sm text-webex-gray-700 dark: webex-gray-300 max-w-md
 *
 * Usage:
 *   <EmptyState
 *     icon={<Icon />}
 *     title="No workspaces yet"
 *     description="Create one to get started."
 *     actions={<Button>Add workspace</Button>}
 *   />
 */
import * as React from "react"
import { cn } from "@lib/cn"

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ className, icon, title, description, actions, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-center text-center px-6 py-12 gap-3",
          className,
        )}
        {...props}
      >
        {icon && (
          <div className="size-12 text-webex-gray-400 dark:text-webex-gray-500 [&>svg]:size-full [&>img]:size-full">
            {icon}
          </div>
        )}
        <div className="text-base font-medium text-webex-gray-1000 dark:text-white">{title}</div>
        {description && (
          <div className="text-sm text-webex-gray-700 dark:text-webex-gray-300 max-w-md">
            {description}
          </div>
        )}
        {actions && <div className="mt-2 flex items-center gap-2">{actions}</div>}
      </div>
    )
  },
)
EmptyState.displayName = "EmptyState"
